import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { api, jsonBody } from "../api";
import { PageHeader } from "../components/PageHeader";
import { StatusChip } from "../components/StatusChip";

export function CleanupPage() {
  const [olderThan, setOlder] = useState(
    new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
  );
  const [clientId, setClient] = useState("");
  const [preview, setPreview] = useState<any>(null);
  const [jobs, setJobs] = useState<any[]>([]);
  const [confirmation, setConfirmation] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [options, setOptions] = useState({
    includeInputs: true,
    includeOcr: true,
    includeResults: true,
  });
  const load = () =>
    api<{ jobs: any[] }>("/admin/cleanup/jobs").then((r) => setJobs(r.jobs));
  useEffect(() => {
    void load();
  }, []);
  const body = {
    olderThan: new Date(`${olderThan}T00:00:00Z`).toISOString(),
    clientId: clientId || null,
  };
  const option = (key: keyof typeof options, label: string) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={options[key]}
          onChange={(e) => setOptions({ ...options, [key]: e.target.checked })}
        />
      }
      label={label}
    />
  );

  return (
    <>
      <PageHeader
        title="Data cleanup"
        subtitle="Preview and remove terminal scan data in audited background jobs."
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Card>
        <CardContent>
          <Stack gap={2}>
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <TextField
                type="date"
                label="Older than"
                value={olderThan}
                onChange={(e) => setOlder(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label="Optional client ID"
                value={clientId}
                onChange={(e) => setClient(e.target.value)}
                sx={{ minWidth: 320 }}
              />
              <Button
                variant="outlined"
                onClick={async () => {
                  try {
                    setPreview(
                      await api("/admin/cleanup/preview", {
                        method: "POST",
                        body: jsonBody(body),
                      }),
                    );
                    setError("");
                  } catch (e) {
                    setError(e instanceof Error ? e.message : "Preview failed");
                  }
                }}
              >
                Preview
              </Button>
            </Stack>
            {preview && (
              <>
                <Alert severity="warning">
                  <strong>{preview.matchedScans}</strong> scans and{" "}
                  <strong>{preview.matchedImages}</strong> images match. Input
                  size: {(preview.inputBytes / 1048576).toFixed(1)} MB.
                </Alert>
                <Stack direction="row" flexWrap="wrap">
                  {option("includeInputs", "Delete inputs")}
                  {option("includeOcr", "Delete Azure JSON")}
                  {option("includeResults", "Delete final results")}
                  <FormControlLabel
                    control={<Checkbox checked disabled />}
                    label="Delete metadata"
                  />
                </Stack>
                <TextField
                  label={`Type "${preview.confirmation}"`}
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                />
                <TextField
                  label="Reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <Button
                  color="error"
                  variant="contained"
                  disabled={
                    confirmation !== preview.confirmation || reason.length < 3
                  }
                  onClick={async () => {
                    try {
                      await api("/admin/cleanup/jobs", {
                        method: "POST",
                        body: jsonBody({
                          ...body,
                          ...options,
                          confirmation,
                          reason,
                        }),
                      });
                      setPreview(null);
                      setConfirmation("");
                      setReason("");
                      void load();
                    } catch (e) {
                      setError(
                        e instanceof Error ? e.message : "Cleanup failed",
                      );
                    }
                  }}
                >
                  Start cleanup job
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
      <Card sx={{ mt: 2.5 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Cleanup history
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Matched</TableCell>
                <TableCell>Deleted scans</TableCell>
                <TableCell>Deleted objects</TableCell>
                <TableCell>Requested by</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>
                    {new Date(job.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <StatusChip value={job.status} />
                  </TableCell>
                  <TableCell>{job.matched_scans}</TableCell>
                  <TableCell>{job.deleted_scans}</TableCell>
                  <TableCell>{job.deleted_objects}</TableCell>
                  <TableCell>{job.requested_by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
